import React, { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { invoiceService } from '../../services/invoiceService';
import { Invoice, InvoiceFilters } from '../../types/invoice';
import { formatCurrency } from '../../utils/formatters';

interface InvoiceListProps {
  filters: InvoiceFilters;
  onView?: (invoice: Invoice) => void;
  onEdit?: (invoice: Invoice) => void;
}

const statusStyle: Record<string, React.CSSProperties> = {
  DRAFT:          { background: 'rgba(120,120,140,0.15)', color: '#9a9ab0', border: '1px solid rgba(120,120,140,0.25)' },
  SENT:           { background: 'rgba(90,140,201,0.12)', color: '#5a8cc9', border: '1px solid rgba(90,140,201,0.25)' },
  VIEWED:         { background: 'rgba(90,140,201,0.12)', color: '#7aacd9', border: '1px solid rgba(90,140,201,0.2)' },
  OVERDUE:        { background: 'rgba(201,90,90,0.12)', color: '#c95a5a', border: '1px solid rgba(201,90,90,0.25)' },
  PAID:           { background: 'rgba(90,201,140,0.12)', color: '#5ac98c', border: '1px solid rgba(90,201,140,0.25)' },
  PARTIALLY_PAID: { background: 'rgba(92,78,51,0.22)', color: '#d97706', border: '1px solid rgba(92,78,51,0.35)' },
  CANCELLED:      { background: 'rgba(80,80,80,0.15)', color: '#555570', border: '1px solid rgba(80,80,80,0.2)' },
};

const statusLabel: Record<string, string> = {
  DRAFT: 'Draft', SENT: 'Sent', VIEWED: 'Viewed', OVERDUE: 'Overdue',
  PAID: 'Paid', PARTIALLY_PAID: 'Partial', CANCELLED: 'Cancelled',
};

const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 500,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  borderBottom: '1px solid rgba(92,78,51,0.15)',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  borderBottom: '1px solid rgba(241,245,249,1)',
  verticalAlign: 'middle',
};

export const InvoiceList: React.FC<InvoiceListProps> = ({ filters, onView, onEdit }) => {
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const queryClient = useQueryClient();

  const { data: invoicesData, isLoading, error } = useQuery({
    queryKey: ['invoices', filters],
    queryFn: () => invoiceService.getInvoices(filters)
  });

  const deleteMutation = useMutation({
    mutationFn: invoiceService.deleteInvoice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-stats'] });
    }
  });

  const markAsSentMutation = useMutation({
    mutationFn: invoiceService.markInvoiceAsSent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-stats'] });
    }
  });

  const cancelMutation = useMutation({
    mutationFn: invoiceService.cancelInvoice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-stats'] });
    }
  });

  const handleSelectInvoice = (invoiceId: string) => {
    setSelectedInvoices(prev =>
      prev.includes(invoiceId) ? prev.filter(id => id !== invoiceId) : [...prev, invoiceId]
    );
  };

  const handleSelectAll = () => {
    const invoiceIds = invoicesData?.invoices.map(inv => inv.id) || [];
    setSelectedInvoices(selectedInvoices.length === invoiceIds.length ? [] : invoiceIds);
  };

  const handleDelete = (invoice: Invoice) => {
    if (window.confirm(`Delete invoice ${invoice.invoiceNumber}?`)) {
      deleteMutation.mutate(invoice.id);
    }
  };

  const handleMarkAsSent = (invoice: Invoice) => {
    if (window.confirm(`Mark invoice ${invoice.invoiceNumber} as sent?`)) {
      markAsSentMutation.mutate(invoice.id);
    }
  };

  const handleCancel = (invoice: Invoice) => {
    if (window.confirm(`Cancel invoice ${invoice.invoiceNumber}?`)) {
      cancelMutation.mutate(invoice.id);
    }
  };

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();

  const isOverdue = (dueDate: string, status: string) =>
    new Date(dueDate) < new Date() && !['PAID', 'CANCELLED'].includes(status);

  if (isLoading) {
    return (
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6 }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 300, color: '#1c2033' }}>Invoices</div>
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[...Array(5)].map((_, i) => (
            <div key={i} style={{ height: 36, background: 'rgba(241,245,249,1)', borderRadius: 3 }} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: '#ffffff', border: '1px solid rgba(201,90,90,0.2)', borderRadius: 6, padding: 24, textAlign: 'center' }}>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#c95a5a' }}>Failed to load invoices</p>
        <button
          onClick={() => queryClient.invalidateQueries({ queryKey: ['invoices'] })}
          style={{ marginTop: 8, fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#d97706', background: 'none', border: 'none', cursor: 'pointer' }}
        >
          Try again
        </button>
      </div>
    );
  }

  const invoices = invoicesData?.invoices || [];

  return (
    <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6 }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 300, color: '#1c2033' }}>Invoices</div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>
            {invoicesData?.pagination.totalItems ?? 0} total invoices
          </div>
        </div>
      </div>

      {invoices.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <svg width="40" height="40" fill="none" stroke="rgba(217,119,6,0.25)" viewBox="0 0 24 24" style={{ margin: '0 auto 12px' }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, letterSpacing: '0.2em', color: '#6b6a5c' }}>NO INVOICES FOUND</p>
        </div>
      ) : (
        <>
          {selectedInvoices.length > 0 && (
            <div style={{ padding: '8px 20px', background: 'rgba(241,245,249,1)', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52' }}>
                {selectedInvoices.length} invoice{selectedInvoices.length !== 1 ? 's' : ''} selected
              </span>
              <button style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a', background: 'none', border: 'none', cursor: 'pointer' }}>
                Bulk Delete
              </button>
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'rgba(201,165,90,0.02)' }}>
                  <th style={{ ...thStyle, width: 40, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectedInvoices.length === invoices.length && invoices.length > 0}
                      onChange={handleSelectAll}
                      style={{ accentColor: '#d97706' }}
                    />
                  </th>
                  <th style={thStyle}>Invoice</th>
                  <th style={thStyle}>Customer</th>
                  <th style={thStyle}>Issue Date</th>
                  <th style={thStyle}>Due Date</th>
                  <th style={thStyle}>Amount</th>
                  <th style={thStyle}>Status</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr
                    key={invoice.id}
                    style={{ transition: 'background 0.15s' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(248,250,252,1)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedInvoices.includes(invoice.id)}
                        onChange={() => handleSelectInvoice(invoice.id)}
                        style={{ accentColor: '#d97706' }}
                      />
                    </td>
                    <td style={tdStyle}>
                      <div style={{ color: '#1c2033', fontWeight: 500 }}>{invoice.invoiceNumber}</div>
                      <div style={{ fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>
                        {invoice.items?.length || 0} item{(invoice.items?.length || 0) !== 1 ? 's' : ''}
                      </div>
                      {invoice.isRecurring && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                          <svg width="11" height="11" fill="none" stroke="#d97706" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          <span style={{ fontSize: 10, color: '#d97706' }}>Recurring</span>
                        </div>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <div>{invoice.customer?.name}</div>
                      <div style={{ fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>{invoice.customer?.email}</div>
                    </td>
                    <td style={tdStyle}>{formatDate(invoice.issueDate)}</td>
                    <td style={tdStyle}>
                      <div style={isOverdue(invoice.dueDate, invoice.status) ? { color: '#c95a5a' } : {}}>
                        {formatDate(invoice.dueDate)}
                        {isOverdue(invoice.dueDate, invoice.status) && (
                          <span style={{ fontSize: 10, marginLeft: 4 }}>(Overdue)</span>
                        )}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, color: '#fbbf24' }}>
                        {formatCurrency(invoice.total)}
                      </div>
                      {invoice.remainingAmount > 0 && (
                        <div style={{ fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>
                          {formatCurrency(invoice.remainingAmount)} remaining
                        </div>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <span style={{
                        ...statusStyle[invoice.status] || statusStyle.DRAFT,
                        display: 'inline-block',
                        padding: '3px 10px',
                        borderRadius: 3,
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 8,
                        letterSpacing: '0.15em',
                        textTransform: 'uppercase',
                      }}>
                        {statusLabel[invoice.status] || invoice.status}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                        <button onClick={() => onView?.(invoice)} style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#d97706', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                          View
                        </button>
                        {invoiceService.canEditInvoice(invoice.status) && (
                          <button onClick={() => onEdit?.(invoice)} style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#5b5a52', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                            Edit
                          </button>
                        )}
                        {invoice.status === 'DRAFT' && (
                          <button onClick={() => handleMarkAsSent(invoice)} style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#5ac98c', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                            Send
                          </button>
                        )}
                        {invoice.status !== 'CANCELLED' && invoice.status !== 'PAID' && (
                          <button onClick={() => handleCancel(invoice)} style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#d97706', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                            Cancel
                          </button>
                        )}
                        {invoiceService.canDeleteInvoice(invoice.status, !!invoice._count?.payments) && (
                          <button onClick={() => handleDelete(invoice)} style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {invoicesData?.pagination && invoicesData.pagination.totalPages > 1 && (
            <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>
                Showing {((invoicesData.pagination.currentPage - 1) * invoicesData.pagination.itemsPerPage) + 1}–{Math.min(invoicesData.pagination.currentPage * invoicesData.pagination.itemsPerPage, invoicesData.pagination.totalItems)} of {invoicesData.pagination.totalItems}
              </span>
              <div style={{ display: 'flex', gap: 4 }}>
                {[...Array(invoicesData.pagination.totalPages)].map((_, i) => {
                  const pageNum = i + 1;
                  const isCurrentPage = pageNum === invoicesData.pagination.currentPage;
                  return (
                    <button
                      key={i}
                      style={{
                        padding: '4px 10px',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 9,
                        letterSpacing: '0.1em',
                        background: isCurrentPage ? 'linear-gradient(135deg, #d97706, #b45309)' : 'rgba(239,232,213,0.5)',
                        color: isCurrentPage ? '#1c2033' : '#8a8778',
                        border: isCurrentPage ? 'none' : '1px solid rgba(92,78,51,0.22)',
                        borderRadius: 3,
                        cursor: 'pointer',
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
